"use client";

import { useEffect, useMemo } from "react";
import { generateSampleFromInputSchema, generateSampleFromSchema } from "../sampleGenerators";
import { getArchetypeConfig } from "./registry";
import type { TriggerArchetypeProps } from "./types";

/**
 * SimpleButton archetype — auto-generated sample data, no user input needed.
 *
 * Used for triggers that don't require user input: manual, cronjob, schedule,
 * anyspend, ramp-*. Auto-selects the payload so the shell's Run button works
 * immediately.
 */
export function SimpleButton({
  triggerType,
  triggerPayload,
  resultSchema,
  inputSchema,
  onRun
}: TriggerArchetypeProps) {
  const hasInputSchema = inputSchema && inputSchema.length > 0;
  const config = getArchetypeConfig(triggerType);
  const samplePayload = useMemo(() => {
    if (hasInputSchema) return generateSampleFromInputSchema(inputSchema);
    return generateSampleFromSchema(resultSchema, triggerType, triggerPayload, config.sampleOverrides);
  }, [hasInputSchema, inputSchema, resultSchema, triggerType, triggerPayload, config.sampleOverrides]);
  const fieldCount = useMemo(() => samplePayload ? Object.keys(samplePayload).length : 0, [samplePayload]);

  // Auto-select payload so the shell's Run button works immediately
  useEffect(() => {
    const payload = fieldCount > 0 ? samplePayload : {};
    onRun(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onRun is a stable state setter
  }, [samplePayload, fieldCount, triggerType]);
  return <p className="body-sm text-ds-text-muted">
      Test data will be generated automatically.
      {fieldCount > 0 && ` (${fieldCount} field${fieldCount !== 1 ? "s" : ""})`}
    </p>;
}